<template>
  <div
    class="recording-controls-zone box-border flex w-17 flex-col items-center gap-2.5 px-1 py-2
           max-sm:w-auto max-sm:flex-row max-sm:gap-[7.3vw] max-sm:p-0"
  >
    <div class="recording-readout box-border w-full px-0.5 text-text-soft max-sm:hidden">
      <div class="recording-meta flex items-center justify-between [font-family:monospace] text-[0.5625rem] font-bold tracking-[0.0625rem]">
        <span class="recording-label tracking-[0.1875rem]">REC</span>
        <span>{{ formattedElapsedTime }}</span>
      </div>
      <div class="microphone-meter mt-1.5 flex h-5 items-end justify-center gap-0.5" :aria-label="$t('ui.recording_controls_microphone_level')">
        <span
          v-for="(level, index) in microphoneLevels"
          :key="index"
          class="microphone-meter-bar h-5 w-1 origin-bottom rounded-t-xs
                 bg-[linear-gradient(180deg,#ff6655_0%,#e33b2e_50%,#7a160f_100%)]
                 shadow-[0_0_0.25rem_rgba(239,68,68,0.32)] [transition:transform_80ms_linear]"
          :style="{ transform: `scaleY(${level})` }"
        ></span>
      </div>
    </div>

    <SimpleButton
      variant="primary"
      class="header-button pause-recording-btn flex! size-17 flex-col justify-center gap-0.75 text-center rounded-tile!
             shadow-none max-sm:size-auto max-sm:text-[1.75rem]"
      @click="emit('toggle-pause')"
      :title="paused ? $t('ui.recording_controls_resume_title') : $t('ui.recording_controls_pause_title')"
    >
      <IconPlaySolid v-if="paused" class="recording-control-icon mb-1 text-[1.3125rem]" />
      <IconPauseSolid v-else class="recording-control-icon mb-1 text-[1.3125rem]" />
      <div class="label text-label font-medium leading-none tracking-[0.03125rem] max-sm:hidden">{{ paused ? 'RESUME' : 'PAUSE' }}</div>
    </SimpleButton>

    <SimpleButton
      class="header-button stop-recording-btn relative flex! size-17 flex-col justify-center gap-0.75 text-center rounded-tile!
             bg-[linear-gradient(160deg,#6d0b0b_0%,#2d0000_90%)] text-white! shadow-none max-sm:size-auto max-sm:text-[1.75rem]"
      @click="emit('stop')"
      :title="$t('ui.recording_controls_stop_title')"
    >
      <div class="pulse-dot mb-1 size-5 animate-pulse-rec rounded-full bg-[#ff2e2e]"></div>
      <div class="label text-label font-medium leading-none tracking-[0.03125rem] max-sm:hidden">STOP</div>
    </SimpleButton>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { IconPauseSolid, IconPlaySolid } from '@iconify-prerendered/vue-heroicons';
import SimpleButton from '@/components/elements/SimpleButton';

defineProps({
  paused: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['toggle-pause', 'stop']);
const METER_BAR_COUNT = 11;
const recording = window.trackliRec;
const elapsedMilliseconds = ref(0);
const microphoneLevels = ref(Array(METER_BAR_COUNT).fill(0.12));

let elapsedTimer = null;
let microphoneAnimationFrame = null;
let microphoneAudioContext = null;
let microphoneSource = null;
let microphoneAnalyser = null;

const formattedElapsedTime = computed(() => {
  const elapsedSeconds = Math.floor(elapsedMilliseconds.value / 1000);
  const minutes = Math.floor(elapsedSeconds / 60).toString().padStart(2, '0');
  const seconds = (elapsedSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
});

function updateElapsedTime() {
  const activePauseDuration = recording.pauseStartedAt ? Date.now() - recording.pauseStartedAt : 0;
  elapsedMilliseconds.value = Date.now()
    - recording.startTime
    - recording.pausedDuration
    - activePauseDuration;
}

function updateMicrophoneLevels() {
  const frequencyData = new Uint8Array(microphoneAnalyser.frequencyBinCount);
  microphoneAnalyser.getByteFrequencyData(frequencyData);
  microphoneLevels.value = microphoneLevels.value.map((_, index) => (
    Math.max(0.12, frequencyData[index + 1] / 255)
  ));
  microphoneAnimationFrame = requestAnimationFrame(updateMicrophoneLevels);
}

onMounted(() => {
  updateElapsedTime();
  elapsedTimer = setInterval(updateElapsedTime, 250);

  const microphoneTrack = recording.mediarec.stream.getAudioTracks()[0];
  if (!microphoneTrack) {
    return;
  }

  microphoneAudioContext = new window.AudioContext();
  microphoneSource = microphoneAudioContext.createMediaStreamSource(new MediaStream([microphoneTrack]));
  microphoneAnalyser = microphoneAudioContext.createAnalyser();
  microphoneAnalyser.fftSize = 64;
  microphoneAnalyser.smoothingTimeConstant = 0.7;
  microphoneSource.connect(microphoneAnalyser);
  microphoneAudioContext.resume().catch(error => {
    console.error('Failed to start the recording microphone meter', error);
  });
  updateMicrophoneLevels();
});

onBeforeUnmount(() => {
  clearInterval(elapsedTimer);
  cancelAnimationFrame(microphoneAnimationFrame);
  if (microphoneAudioContext) {
    microphoneSource.disconnect();
    microphoneAnalyser.disconnect();
    microphoneAudioContext.close().catch(error => {
      console.error('Failed to close the recording microphone meter', error);
    });
  }
});
</script>
